import React, { useState } from 'react';
import { Send, MapPin, Phone, Mail, Calendar, Users, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { createQuote } from '../api';

const Contact = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    eventType: '',
    eventDate: '',
    guestCount: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await createQuote(formData);
      
      if (response.success) {
        toast.success(response.message);
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          eventType: '',
          eventDate: '',
          guestCount: '',
          message: ''
        });
      }
    } catch (error) {
      toast.error(t('contact.form.error'));
      console.error('Error submitting quote:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-gradient-to-b from-white to-fuchsia-50">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold mb-6">
            <span className="bg-gradient-to-r from-fuchsia-600 via-pink-600 to-orange-500 bg-clip-text text-transparent">
              {t('contact.title')}
            </span>
          </h2>
          <p className="text-xl text-gray-600">
            {t('contact.subtitle')}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Contact Form */}
          <Card className="shadow-2xl border-0">
            <CardHeader>
              <CardTitle className="text-2xl">{t('contact.form.title')}</CardTitle>
              <CardDescription>
                {t('contact.form.description')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Name */}
                <div className="space-y-2">
                  <Label htmlFor="name">{t('contact.form.name')} *</Label>
                  <Input
                    id="name"
                    type="text"
                    placeholder={t('contact.form.namePlaceholder')}
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    required
                    className="h-12"
                  />
                </div>

                {/* Email & Phone */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">{t("contact.form.email")} *</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder={t("contact.form.emailPlaceholder")}
                      value={formData.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      required
                      className="h-12"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">{t("contact.form.phone")}</Label>
                    <Input
                      id="phone"
                      type="tel"
                      placeholder={t("contact.form.phonePlaceholder")}
                      value={formData.phone}
                      onChange={(e) => handleChange('phone', e.target.value)}
                      className="h-12"
                    />
                  </div>
                </div>

                {/* Event Type */}
                <div className="space-y-2">
                  <Label htmlFor="eventType">{t('contact.form.eventType')} *</Label>
                  <Select
                    value={formData.eventType}
                    onValueChange={(value) => handleChange('eventType', value)}
                    required
                  >
                    <SelectTrigger className="h-12">
                      <SelectValue placeholder={t("contact.form.eventTypePlaceholder")} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="wedding">{t("contact.eventTypes.wedding")}</SelectItem>
                      <SelectItem value="birthday">{t("contact.eventTypes.birthday")}</SelectItem>
                      <SelectItem value="corporate">{t("contact.eventTypes.corporate")}</SelectItem>
                      <SelectItem value="babyshower">{t("contact.eventTypes.babyShower")}</SelectItem>
                      <SelectItem value="inauguration">{t("contact.eventTypes.graduation")}</SelectItem>
                      <SelectItem value="balloonDrop">{t("contact.eventTypes.balloonDrop")}</SelectItem>
                      <SelectItem value="newYear">{t("contact.eventTypes.newYear")}</SelectItem>
                      <SelectItem value="halloween">{t("contact.eventTypes.halloween")}</SelectItem>
                      <SelectItem value="dj">{t("contact.eventTypes.dj")}</SelectItem>
                      <SelectItem value="other">{t("contact.eventTypes.other")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Event Date & Guest Count */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="eventDate">{t("contact.form.eventDate")}</Label>
                    <Input
                      id="eventDate"
                      type="date"
                      value={formData.eventDate}
                      onChange={(e) => handleChange('eventDate', e.target.value)}
                      className="h-12"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="guestCount">{t("contact.form.guests")}</Label>
                    <Input
                      id="guestCount"
                      type="number"
                      placeholder="Ex: 50"
                      value={formData.guestCount}
                      onChange={(e) => handleChange('guestCount', e.target.value)}
                      className="h-12"
                    />
                  </div>
                </div>

                {/* Message */}
                <div className="space-y-2">
                  <Label htmlFor="message">{t("contact.form.message")}</Label>
                  <Textarea
                    id="message"
                    placeholder="Décrivez votre projet, vos envies, votre budget..."
                    value={formData.message}
                    onChange={(e) => handleChange('message', e.target.value)}
                    rows={5}
                    className="resize-none"
                  />
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-orange-500 via-pink-600 to-fuchsia-600 hover:from-orange-600 hover:via-pink-700 hover:to-fuchsia-700 text-white h-12 rounded-full text-lg font-medium shadow-lg hover:shadow-xl transition-all group disabled:opacity-50"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 w-5 h-5 animate-spin" />
                      {t("contact.form.submitting")}
                    </>
                  ) : (
                    <>
                      {t("contact.form.submit")}
                      <Send className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Contact Info */}
          <div className="space-y-6">
            {/* Info Card */}
            <Card className="shadow-xl border-0 bg-gradient-to-br from-pink-50 to-purple-50">
              <CardHeader>
                <CardTitle className="text-2xl">{t("contact.info.title")}</CardTitle>
                <CardDescription>
                  N'hésitez pas à nous contacter directement
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-orange-500 to-pink-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <div className="font-bold text-gray-900 mb-1">{t("contact.form.phone")}</div>
                    <div className="text-gray-600">(514) 321-5555</div>
                    <div className="text-sm text-gray-500">{t("contact.info.hours")}</div>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-pink-500 to-fuchsia-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <div className="font-bold text-gray-900 mb-1">Email</div>
                    <div className="text-gray-600">martindoucet184@gmail.com</div>
                    <div className="text-sm text-gray-500">Réponse sous 24h</div>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-fuchsia-500 to-orange-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <div className="font-bold text-gray-900 mb-1">{t("contact.info.address")}</div>
                    <div className="text-gray-600">Montréal, Québec</div>
                    <div className="text-gray-600">Canada</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Why Choose Us */}
            <Card className="shadow-xl border-0">
              <CardHeader>
                <CardTitle className="text-2xl">{t("contact.info.whyChoose")}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-3">
                  <Calendar className="w-6 h-6 text-pink-500 flex-shrink-0" />
                  <span className="text-gray-700">{t("contact.info.response24h")}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Users className="w-6 h-6 text-purple-500 flex-shrink-0" />
                  <span className="text-gray-700">{t("contact.info.creativeTeam")}</span>
                </div>
                <div className="flex items-center gap-3">
                  <MapPin className="w-6 h-6 text-yellow-500 flex-shrink-0" />
                  <span className="text-gray-700">{t("contact.info.delivery")}</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
